import { io, Socket } from 'socket.io-client';

/**
 * Global WebSocket Manager (Socket.IO client)
 *
 * Mirrors the reference implementation documented in WS_CONNECT.md:
 * - Connects with `socket.io-client` (transports: websocket + polling fallback,
 *   infinite reconnection attempts, 2s reconnection delay).
 * - On `connect`: sets `localStorage['socket_status'] = true` and emits
 *   `"client"` with `{ data: { accountID, access_token } }`.
 * - On `connect_error`: sets `localStorage['socket_status'] = false`.
 * - Listens for `"clientData"` (main data event) and `"errorResponse"`.
 * - Re-emits `"client"` whenever the active account changes.
 *
 * The connection URL is `{WS base}/{brand}` where `{brand}` is the same
 * domain-derived brand used for the REST API (namespace per brand).
 */

type MessageHandler = (data: unknown) => void;

export type WSSubscriptionTopic =
  | 'clientData'
  | 'errorResponse'
  | 'account.update'
  | 'positions.update'
  | 'orders.update'
  | 'market.tick'
  | 'notifications'
  | 'balance.update'
  | string;

type WSStatus = 'disconnected' | 'connecting' | 'connected' | 'error';

const DEFAULT_WS_URL = 'wss://socket.fxcrm.live:2096/';

class WebSocketManager {
  private socket: Socket | null = null;
  private statusListeners: Set<(status: WSStatus) => void> = new Set();
  private status: WSStatus = 'disconnected';
  private accessToken: string | null = null;
  private accountId: string | null = null;
  /** Registered topic handlers, kept independent of the socket instance so
   *  subscribe() calls made before connect() (or across reconnects) still work. */
  private subscriptions: Map<WSSubscriptionTopic, Set<MessageHandler>> = new Map();

  private getWSBaseURL(): string {
    if (typeof localStorage !== 'undefined') {
      const stored = localStorage.getItem('ws_url');
      if (stored) return stored;
    }
    return DEFAULT_WS_URL;
  }

  private setStatus(status: WSStatus) {
    this.status = status;
    this.statusListeners.forEach((listener) => listener(status));
  }

  getStatus(): WSStatus {
    return this.status;
  }

  onStatusChange(listener: (status: WSStatus) => void): () => void {
    this.statusListeners.add(listener);
    return () => this.statusListeners.delete(listener);
  }

  /**
   * Connect (or reuse the existing connection) and emit the "client" event.
   */
  connect(accessToken: string, accountId: string) {
    this.accessToken = accessToken;
    this.accountId = accountId;

    if (this.socket?.connected) {
      this._emitClientEvent();
      return;
    }
    if (this.socket) {
      // Already connecting — let the 'connect' handler emit once ready.
      return;
    }

    if (typeof window === 'undefined') return;

    this.setStatus('connecting');

    const url = this.getWSBaseURL().replace(/\/+$/, '');

    this.socket = io(url, {
      transports: ['websocket', 'polling'],
      autoConnect: true,
      reconnection: true,
      reconnectionAttempts: Infinity,
      reconnectionDelay: 2000,
    });

    // Re-attach any handlers registered before this socket instance existed.
    this.subscriptions.forEach((handlers, topic) => {
      handlers.forEach((handler) => this.socket?.on(topic, handler));
    });

    this.socket.on('connect', () => {
      this.setStatus('connected');
      if (typeof localStorage !== 'undefined') {
        localStorage.setItem('socket_status', 'true');
      }
      this._emitClientEvent();
    });

    this.socket.on('connect_error', (err) => {
      this.setStatus('error');
      if (typeof localStorage !== 'undefined') {
        localStorage.setItem('socket_status', 'false');
      }
      console.error('❌ Socket hata:', err);
    });

    this.socket.on('disconnect', () => {
      this.setStatus('disconnected');
      if (typeof localStorage !== 'undefined') {
        localStorage.setItem('socket_status', 'false');
      }
    });
  }

  /**
   * Emit the 'client' subscription event to the server.
   * Called on connect and on account change.
   */
  private _emitClientEvent() {
    const token =
      this.accessToken ||
      (typeof localStorage !== 'undefined' ? localStorage.getItem('access_token') : null);
    const accId =
      this.accountId ||
      (typeof localStorage !== 'undefined' ? localStorage.getItem('account_id') : null);

    if (!token || !accId || !this.socket?.connected) return;

    this.socket.emit('client', {
      data: {
        accountID: accId,
        access_token: token,
      },
    });
  }

  /**
   * Re-emit the client event when account changes.
   * Call this after switchAccount.
   */
  emitAccountChanged(accountId: string) {
    this.accountId = accountId;
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem('account_id', accountId);
    }
    this._emitClientEvent();
  }

  /**
   * Subscribe to a topic (Socket.IO event). Returns an unsubscribe function.
   */
  subscribe(topic: WSSubscriptionTopic, handler: MessageHandler): () => void {
    if (!this.subscriptions.has(topic)) {
      this.subscriptions.set(topic, new Set());
    }
    this.subscriptions.get(topic)!.add(handler);
    this.socket?.on(topic, handler);

    return () => {
      this.subscriptions.get(topic)?.delete(handler);
      this.socket?.off(topic, handler);
    };
  }

  /**
   * Emit a raw event to the server.
   */
  send(eventName: string, payload: unknown) {
    this.socket?.emit(eventName, payload);
  }

  /**
   * Disconnect and stop reconnecting.
   */
  disconnect() {
    if (this.socket) {
      this.socket.removeAllListeners();
      this.socket.disconnect();
      this.socket = null;
    }
    this.setStatus('disconnected');
  }
}

// Singleton instance
export const wsManager = new WebSocketManager();

export default wsManager;
